"use client";

import { c as _c } from "react/compiler-runtime";
import { api } from "@agroproj/backend/convex/_generated/api";
import { SignOutButton, useUser } from "@clerk/nextjs";
import { useQuery } from "convex/react";
import { CheckIcon, LifeBuoyIcon, LogOut, MonitorIcon, Moon, MoreHorizontal, Sun, UserIcon, type LucideIcon } from "lucide-react";
import type { Route } from "next";
import { useTheme } from "next-themes";
import Link from "next/link";
import { useMemo } from "react";
import { useOpenSupportDialog } from "@/components/support/support-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuLinkItem, DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import { Skeleton } from "@/components/ui/skeleton";
import { useIsHydrated } from "@/hooks/use-is-hydrated";
import { buildOrganizationPath } from "@/lib/organization-path";
import { getPlanShortLabel } from "@/lib/pricing-display";
import { cn } from "@/lib/utils";
import { OrganizationSwitcher } from "./organization-switcher";
type ThemeValue = "light" | "dark" | "system";
const THEME_OPTIONS: {
  value: ThemeValue;
  icon: LucideIcon;
  label: string;
}[] = [{
  value: "light",
  icon: Sun,
  label: "Claro"
}, {
  value: "dark",
  icon: Moon,
  label: "Escuro"
}, {
  value: "system",
  icon: MonitorIcon,
  label: "Sistema"
}];
function getInitials(firstName?: string | null, lastName?: string | null): string {
  const first = firstName?.trim().charAt(0) ?? "";
  const last = lastName?.trim().charAt(0) ?? "";
  const initials = `${first}${last}`.toUpperCase();
  return initials || "?";
}
export function NavUser() {
  const $ = _c(32);
  const {
    isMobile,
    state
  } = useSidebar();
  const isCollapsed = state === "collapsed" && !isMobile;
  const {
    isLoaded,
    user
  } = useUser();
  const openSupportDialog = useOpenSupportDialog();
  const {
    setTheme,
    theme
  } = useTheme();
  const isHydrated = useIsHydrated();
  const organizationStatus = useQuery(api.check_membership.hasOrganization);
  const organizationSlug = organizationStatus?.activeOrganizationSlug;
  const hasActiveSubscription = organizationStatus?.hasActiveSubscription === true;
  const canReadBilling = organizationStatus?.isAuthenticated === true && organizationStatus.hasOrganization;
  let t0;
  if ($[0] !== canReadBilling) {
    t0 = canReadBilling ? {} : "skip";
    $[0] = canReadBilling;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const billingStatus = useQuery(api.payments.queries.getOrganizationBillingStatus, t0);
  let t1;
  if ($[2] !== canReadBilling) {
    t1 = canReadBilling ? {} : "skip";
    $[2] = canReadBilling;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const plans = useQuery(api.payments.catalog.listPublicSubscriptionPlans, t1);
  let t2;
  if ($[4] !== billingStatus?.billing || $[5] !== billingStatus?.hasActiveSubscription || $[6] !== plans) {
    bb0: {
      const billing = billingStatus?.billing ?? null;
      if (!billing) {
        t2 = billingStatus?.hasActiveSubscription === false ? "Sem plano" : null;
        break bb0;
      }
      const prices = (plans ?? []).flatMap(_temp);
      const matched = prices.find(t3 => {
        const {
          price: price_1
        } = t3;
        return price_1._id === billing.subscriptionPriceId;
      }) ?? prices.find(t4 => {
        const {
          price: price_0
        } = t4;
        return price_0.providerPriceId === (billing.providerPriceId ?? billing.stripePriceId);
      });
      if (matched) {
        t2 = getPlanShortLabel({
          priceMetadata: matched.price.metadata,
          productMetadata: matched.product.metadata,
          unitAmount: matched.price.unitAmount
        }) ?? matched.product.name;
        break bb0;
      }
      if (billing.provider === "internal") {
        t2 = "Gratuito";
        break bb0;
      }
      if (billingStatus?.hasActiveSubscription) {
        t2 = "Pago";
        break bb0;
      }
      t2 = "Sem plano";
    }
    $[4] = billingStatus?.billing;
    $[5] = billingStatus?.hasActiveSubscription;
    $[6] = plans;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const planLabel = t2;
  const t3 = `${user?.firstName ?? ""} ${user?.lastName ?? ""}`;
  let t4;
  if ($[8] !== t3) {
    t4 = t3.trim();
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const userName = t4;
  const userEmail = user?.emailAddresses[0]?.emailAddress ?? "";
  const t5 = user?.firstName;
  const t6 = user?.lastName;
  let t7;
  if ($[10] !== t5 || $[11] !== t6) {
    t7 = getInitials(t5, t6);
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const initials = t7;
  const activeTheme = (theme ?? "system") as ThemeValue;
  let t8;
  if ($[13] !== activeTheme) {
    t8 = THEME_OPTIONS.find(option => option.value === activeTheme)?.icon ?? Sun;
    $[13] = activeTheme;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const currentThemeIcon = t8;
  const CurrentThemeIcon = isHydrated ? currentThemeIcon : Sun;
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <SidebarMenuItem className="hidden"><OrganizationSwitcher /></SidebarMenuItem>;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== CurrentThemeIcon || $[17] !== activeTheme || $[18] !== hasActiveSubscription || $[19] !== initials || $[20] !== isCollapsed || $[21] !== isHydrated || $[22] !== isLoaded || $[23] !== isMobile || $[24] !== openSupportDialog || $[25] !== organizationSlug || $[26] !== planLabel || $[27] !== setTheme || $[28] !== user?.imageUrl || $[29] !== userEmail || $[30] !== userName) {
    t10 = <SidebarMenu>{t9}<SidebarMenuItem className="group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:justify-center">{!isLoaded ? <div aria-busy="true" className="flex h-12 w-full items-center gap-2 rounded-lg px-2 group-data-[collapsible=icon]:size-8 group-data-[collapsible=icon]:px-0"><Skeleton className="size-8 shrink-0 rounded-lg" /><div className={cn("flex flex-1 flex-col gap-1.5", isCollapsed && "hidden")}><Skeleton className="h-3 w-24" /><Skeleton className="h-3 w-32" /></div></div> : <DropdownMenu><DropdownMenuTrigger render={<SidebarMenuButton className="bg-surface hover:bg-muted/50 hover:text-foreground data-open:bg-muted/50 data-open:text-foreground h-auto min-h-12 rounded-lg py-1.5 transition-colors group-data-[collapsible=icon]:mx-auto group-data-[collapsible=icon]:size-8 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:gap-0 group-data-[collapsible=icon]:overflow-visible group-data-[collapsible=icon]:p-0!" size="lg" tooltip={planLabel ? `${userName} · ${planLabel}` : userName} />}><Avatar className="size-8 rounded-lg"><AvatarImage alt={userName} src={user?.imageUrl} /><AvatarFallback className="bg-muted text-sidebar-foreground rounded-lg font-semibold">{initials}</AvatarFallback></Avatar><div className={cn("flex min-w-0 flex-1 flex-col items-start gap-0.5 text-left text-xs/relaxed", isCollapsed && "hidden")}><span className="text-sidebar-foreground truncate font-semibold">{userName}</span>{planLabel ? <Badge variant={planLabel === "Gratuito" || planLabel === "Sem plano" ? "outline" : "status-active"}>{planLabel}</Badge> : null}</div><div className={cn("text-muted-foreground flex items-center gap-2", isCollapsed && "hidden")}><MoreHorizontal className="size-4" /></div></DropdownMenuTrigger><DropdownMenuContent align="end" className="w-(--anchor-width) min-w-56 rounded-lg" side={isMobile ? "bottom" : "right"} sideOffset={4}><DropdownMenuGroup><DropdownMenuLabel className="p-0 font-normal"><div className="flex items-center gap-2 px-1 py-1.5 text-left"><Avatar className="size-8 rounded-lg"><AvatarImage alt={userName} src={user?.imageUrl} /><AvatarFallback className="rounded-lg">{initials}</AvatarFallback></Avatar><div className="grid flex-1 text-left text-xs/relaxed"><span className="truncate font-semibold">{userName}</span><span className="text-muted-foreground truncate">{userEmail}</span>{planLabel ? <Badge className="mt-1" variant={planLabel === "Gratuito" || planLabel === "Sem plano" ? "outline" : "status-active"}>{planLabel}</Badge> : null}</div></div></DropdownMenuLabel></DropdownMenuGroup><DropdownMenuSeparator /><DropdownMenuGroup>{organizationSlug && hasActiveSubscription ? <DropdownMenuLinkItem render={<Link href={buildOrganizationPath(organizationSlug, "/settings/account") as Route} />}><UserIcon />Conta</DropdownMenuLinkItem> : null}<DropdownMenuLinkItem render={<Link href="/assistant-privacy" />}><UserIcon />Privacidade das conversas</DropdownMenuLinkItem><DropdownMenuSub><DropdownMenuSubTrigger><CurrentThemeIcon className="size-4" /><span>Tema</span></DropdownMenuSubTrigger><DropdownMenuSubContent>{THEME_OPTIONS.map(t11 => {
                    const {
                      value,
                      icon: Icon,
                      label
                    } = t11;
                    return <DropdownMenuItem key={value} onClick={() => setTheme(value)}><Icon className="size-4" /><span className="flex-1">{label}</span>{isHydrated && activeTheme === value && <CheckIcon className="text-sidebar-active-indicator" />}</DropdownMenuItem>;
                  })}</DropdownMenuSubContent></DropdownMenuSub></DropdownMenuGroup>{organizationSlug ? <><DropdownMenuSeparator /><DropdownMenuItem onClick={openSupportDialog}><LifeBuoyIcon />Suporte</DropdownMenuItem></> : null}<DropdownMenuSeparator /><SignOutButton redirectUrl="/"><DropdownMenuItem><LogOut />Sair</DropdownMenuItem></SignOutButton></DropdownMenuContent></DropdownMenu>}</SidebarMenuItem></SidebarMenu>;
    $[16] = CurrentThemeIcon;
    $[17] = activeTheme;
    $[18] = hasActiveSubscription;
    $[19] = initials;
    $[20] = isCollapsed;
    $[21] = isHydrated;
    $[22] = isLoaded;
    $[23] = isMobile;
    $[24] = openSupportDialog;
    $[25] = organizationSlug;
    $[26] = planLabel;
    $[27] = setTheme;
    $[28] = user?.imageUrl;
    $[29] = userEmail;
    $[30] = userName;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  return t10;
}
function _temp(plan) {
  return plan.prices.map(price => ({
    product: plan.product,
    price
  }));
}